import type { SsrAppApiDirectOptions } from '@/data/appApi';
import { Prices } from '@/store/prices/types';
import apiRequest from '@/utils/apiRequest';
import { RequestUserData } from '@/utils/apiRequest/types';
import { calmLogger } from '@/utils/calmLogger';

import { PriceCatalogResponse } from '../../utils/prices/mergeCatalogPriceIntoPrices';

export const FAMILY_PLAN_YEARLY_PLAN_ID = 'family_plan_1y_autorenew';
const FAMILY_PRICING_ENDPOINT = `stripe-price-catalog/price?quantity=1&plan=${FAMILY_PLAN_YEARLY_PLAN_ID}`;

async function fetchFamilyPrice(
	endpoint: string,
	userData: RequestUserData,
	opts: SsrAppApiDirectOptions | undefined,
	hasCoupon: boolean,
): Promise<PriceCatalogResponse | null> {
	try {
		if (typeof window === 'undefined' && opts?.ctx) {
			// SSR app-api-direct: see getFeatureFlags.ts. `requiresAuth: true`
			// matches the authproxy (Bearer injected for this catalog endpoint).
			const { appApiServerRequest } = await import('@/data/appApi');
			const { data } = await appApiServerRequest<PriceCatalogResponse>({
				endpoint,
				ctx: opts.ctx,
				xLanguage: opts.xLanguage,
				requiresAuth: true,
			});
			return data || null;
		}

		const response = await apiRequest<PriceCatalogResponse>({
			endpoint,
			method: 'GET',
			userData,
		});
		return response?.data || null;
	} catch (err) {
		// The coupon stays out of the message so errors group, and partner codes stay out of logs.
		calmLogger.error(
			`Error retrieving family prices via GET /${FAMILY_PRICING_ENDPOINT}`,
			{ hasCoupon },
			err,
		);
		return null;
	}
}

export async function getFamilyPrices(
	userData: RequestUserData,
	opts?: SsrAppApiDirectOptions,
	coupon?: string | null,
): Promise<PriceCatalogResponse | null> {
	if (coupon) {
		const endpoint = `${FAMILY_PRICING_ENDPOINT}&coupon=${encodeURIComponent(coupon)}`;
		const discounted = await fetchFamilyPrice(endpoint, userData, opts, true);
		// A coupon the catalog rejects must not take the family list price down with it.
		if (discounted) return discounted;
	}
	return fetchFamilyPrice(FAMILY_PRICING_ENDPOINT, userData, opts, false);
}

// The list price, because a 100%-off coupon makes the current family price 0.
export const isFamilyPricingAvailable = (prices?: Prices) => {
	return Boolean(prices?.original?.family);
};
